import React, { useMemo, useState } from "react";
import axios from "axios";

const api = axios.create({ withCredentials: true });

export default function SuspiciousUsersPanel({ rows = [], onRefresh }) {
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });

  const selectedRows = useMemo(
    () => rows.filter((row) => selected[row.id]),
    [rows, selected]
  );

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => compareValues(a, b, sort.key, sort.dir));
    return copy;
  }, [rows, sort]);

  const toggleAll = () => {
    if (selectedRows.length === rows.length) {
      setSelected({});
      return;
    }
    const next = {};
    rows.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };

  const toggleOne = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  };

  const deleteSelected = async () => {
    if (!selectedRows.length) return;
    setBusy(true);
    try {
      await api.post("/api/admin/security/users/delete", {
        ids: selectedRows.map((x) => x.id),
      });
      setSelected({});
      onRefresh?.();
    } finally {
      setBusy(false);
    }
  };

  const blockSelectedIps = async () => {
    const ips = [...new Set(selectedRows.map((x) => x.ip).filter(Boolean))];
    if (!ips.length) return;
    setBusy(true);
    try {
      await api.post("/api/admin/security/ips/block", {
        ips,
        reason: "manual_admin_block",
      });
      onRefresh?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={styles.topRow}>
        <h2 style={styles.title}>Suspicious recent users</h2>
        <div style={styles.actions}>
          <button type="button" style={styles.blockBtn} disabled={busy || !selectedRows.length} onClick={blockSelectedIps}>Block selected IPs</button>
          <button type="button" style={styles.dangerBtn} disabled={busy || !selectedRows.length} onClick={deleteSelected}>Delete selected users</button>
        </div>
      </div>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}><input type="checkbox" onChange={toggleAll} checked={rows.length > 0 && selectedRows.length === rows.length} /></th>
              <th style={styles.th}>{sortHeader("ID", "id", sort, handleSort)}</th>
              <th style={styles.th}>{sortHeader("Username", "name", sort, handleSort)}</th>
              <th style={styles.th}>{sortHeader("Email", "email", sort, handleSort)}</th>
              <th style={styles.th}>{sortHeader("IP", "ip", sort, handleSort)}</th>
              <th style={styles.th}>{sortHeader("Created", "created_at", sort, handleSort)}</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length ? sortedRows.map((row) => (
              <tr key={row.id}>
                <td style={styles.td}><input type="checkbox" checked={!!selected[row.id]} onChange={() => toggleOne(row.id)} /></td>
                <td style={styles.td}>{row.id}</td>
                <td style={styles.td}>{row.name || row.username || "-"}</td>
                <td style={styles.td}>{row.email || "-"}</td>
                <td style={styles.td}>{row.ip || "-"}</td>
                <td style={styles.td}>{formatDate(row.created_at)}</td>
              </tr>
            )) : (
              <tr><td style={styles.empty} colSpan={6}>No suspicious users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function compareValues(a, b, key, dir) {
  const mult = dir === "asc" ? 1 : -1;
  if (key === "id") return mult * ((Number(a.id || 0)) - (Number(b.id || 0)));
  if (key === "created_at") return mult * (toTime(a.created_at) - toTime(b.created_at));
  if (key === "ip") return mult * compareIp(a.ip, b.ip);
  return mult * compareText(a[key] ?? a.username, b[key] ?? b.username);
}

function compareText(a, b) {
  return String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base", numeric: true });
}

function compareIp(a, b) {
  const pa = String(a || "").split(".").map((x) => Number(x) || 0);
  const pb = String(b || "").split(".").map((x) => Number(x) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff) return diff;
  }
  return 0;
}

function toTime(value) {
  const d = new Date(value || 0);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function sortHeader(label, key, sort, onSort) {
  const active = sort.key === key;
  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "↕";
  return (
    <button type="button" onClick={() => onSort(key)} style={styles.sortBtn}>
      {label} <span style={styles.arrow}>{arrow}</span>
    </button>
  );
}

const styles = {
  topRow: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" },
  title: { margin: 0, fontSize: 18, fontWeight: 800 },
  actions: { display: "flex", gap: 10, flexWrap: "wrap" },
  sortBtn: { background: "transparent", border: "none", padding: 0, fontWeight: 800, cursor: "pointer", color: "#111" },
  arrow: { fontSize: 11, marginLeft: 4, color: "#111" },
  blockBtn: { background: "#0d5d6c", color: "#fff", border: "none", borderRadius: 12, padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  dangerBtn: { background: "#b42318", color: "#fff", border: "none", borderRadius: 12, padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "14px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: 800, whiteSpace: "nowrap", color: "#111" },
  td: { padding: "14px 10px", borderBottom: "1px solid #f0f0f0", verticalAlign: "top" },
  empty: { padding: 20, textAlign: "center", color: "#667085" },
};
